// src/components/CondensedPagination.js
//
// Shared presentational pagination component used across patient and staff
// tables. Renders a condensed page-number strip that scales to thousands of
// pages without visual clutter.
//
// WHAT IT DISPLAYS:
// - A record counter: "Showing 1–10 of 3,241 patients"
// - Previous / Next navigation buttons (disabled at boundaries)
// - A condensed set of page number buttons (first, last, current, +/- 2
//   neighbours) with ellipsis markers for hidden ranges
//
// DESIGN DECISIONS:
// - The component does NOT own pagination state. The parent table owns
//   currentPage (via useState) and passes it down along with onPageChange.
//   This keeps state management co-located with the filtering/sorting logic
//   that determines page boundaries, and avoids duplicating reset/clamp logic.
// - pageSize is accepted as a prop (not hardcoded) so the component supports
//   future page-size configurability without requiring changes here.
// - Responsive collapse: at < md (768px), neighbour page buttons are hidden
//   via Tailwind's `hidden md:inline-flex` class. Only first, current, and last
//   page buttons remain visible on small screens. This avoids duplicating DOM
//   nodes and keeps a single accessibility tree (consistent with codebase
//   conventions — standard Tailwind responsive prefixes, no container queries).
// - The page-number generation algorithm lives in src/utils/paginationUtils.js
//   as a pure function, enabling exhaustive unit testing independent of React.

import React from 'react';
import { getPageNumbers } from '../utils/paginationUtils';

/**
 * CondensedPagination — a scalable pagination bar for data tables.
 *
 * @param {object} props
 * @param {number} props.currentPage   1-indexed active page.
 * @param {number} props.totalPages    Total page count.
 * @param {number} props.totalItems    Total record count (for the counter label).
 * @param {number} props.pageSize      Items per page.
 * @param {(page: number) => void} props.onPageChange  Callback when user navigates.
 * @param {string} [props.itemLabel]   Optional noun for the counter (e.g. "patients").
 */
function CondensedPagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  itemLabel = '',
}) {
  // Calculate the range of items shown on the current page
  const indexOfFirst = (currentPage - 1) * pageSize + 1;
  const indexOfLast = Math.min(currentPage * pageSize, totalItems);

  // Format numbers with locale-appropriate thousand separators (e.g. 3,241)
  const formattedFirst = indexOfFirst.toLocaleString();
  const formattedLast = indexOfLast.toLocaleString();
  const formattedTotal = totalItems.toLocaleString();

  // Build the label suffix — include the item noun when provided
  const labelSuffix = itemLabel ? ` ${itemLabel}` : '';

  // Generate the condensed page-number sequence from the pure utility
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  // Shared button classes — inline-flex keeps numbers vertically centred inside
  // the fixed min-height box (plain padding alone can look off with Poppins).
  // Transitions use ease-in-out across colour, border, shadow, and a subtle
  // scale on press so page changes feel soft rather than abrupt.
  const baseButtonClass =
    'inline-flex items-center justify-center px-3 min-w-9 min-h-9 text-sm leading-none ' +
    'border rounded transition-all duration-300 ease-in-out ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1';
  const activeClass =
    'font-bold text-white bg-primary border-transparent shadow-sm';
  const inactiveClass =
    'font-medium text-slate-500 bg-white border-slate-200 ' +
    'hover:bg-slate-50 hover:border-slate-300 hover:shadow-sm ' +
    'active:scale-[0.97] active:bg-slate-100';
  const disabledClass =
    'font-medium text-slate-300 bg-white border-slate-200 cursor-not-allowed';

  return (
    <div className="flex justify-between items-center px-3 py-2 border-t border-slate-200">
      {/* Record counter — always visible, even on single-page results */}
      <div className="text-xs text-slate-500">
        Showing{' '}
        <b>
          {formattedFirst}&#8211;{formattedLast}
        </b>{' '}
        of {formattedTotal}
        {labelSuffix}
      </div>

      {/* Navigation controls — only rendered when there are multiple pages */}
      {totalPages > 1 && (
        <nav aria-label="Pagination">
          <div className="flex items-center space-x-1">
            {/* Previous button */}
            <button
              onClick={() => !isFirstPage && onPageChange(currentPage - 1)}
              disabled={isFirstPage}
              aria-label="Previous page"
              className={`${baseButtonClass} ${isFirstPage ? disabledClass : inactiveClass}`}
            >
              Prev
            </button>

            {/* Page number buttons — single strip with responsive visibility.
                On screens < md, only first, current, last, and ellipsis show.
                Neighbour pages (not first/current/last) are hidden below md. */}
            {pageNumbers.map((item, index) => {
              if (item === 'ellipsis') {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="px-2 py-1 min-w-9 min-h-9 text-sm text-slate-400 select-none flex items-center justify-center"
                    aria-hidden="true"
                  >
                    &hellip;
                  </span>
                );
              }

              // Determine if this button is a "neighbour" that should be hidden
              // on small screens. First page, last page, and current page are
              // always visible; everything else collapses on mobile.
              const isAlwaysVisible =
                item === 1 || item === totalPages || item === currentPage;
              const responsiveClass = isAlwaysVisible ? '' : ' hidden md:inline-flex';
              const isActive = item === currentPage;

              return (
                <button
                  key={item}
                  onClick={() => onPageChange(item)}
                  aria-label={`Go to page ${item}`}
                  aria-current={isActive ? 'page' : undefined}
                  className={`${baseButtonClass} ${
                    isActive ? activeClass : inactiveClass
                  }${responsiveClass}`}
                >
                  {item}
                </button>
              );
            })}

            {/* Next button */}
            <button
              onClick={() => !isLastPage && onPageChange(currentPage + 1)}
              disabled={isLastPage}
              aria-label="Next page"
              className={`${baseButtonClass} ${isLastPage ? disabledClass : inactiveClass}`}
            >
              Next
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}

export default CondensedPagination;
